import { from, ReplaySubject, EMPTY, Subscription, timer } from 'rxjs';
import { distinctUntilChanged, shareReplay, map, switchMap, filter, withLatestFrom, delayWhen, catchError, } from 'rxjs/operators';
import { HttpError, isTestEnv, lastValueFrom } from '@onviobr/helpers';
import { createSessionWithJWT, createJWT, createSession, retrieveSessionWithSSO, deleteSession, getSessionV1, getIACs, getFRPsV1, getSessionAndBindingsV1, getCompanyID, getContactID, getVerticalRoute, getEnvironmentV1, } from './security-services.js';
import { SecureHttp } from './secure-http.js';
import * as frps from './feature-resource-permissions.js';
import * as iacs from './infrastructure-access-controls.js';
import { hasFirmEveryLicense, hasFirmSomeLicense } from './licenses.js';
const STORAGE_TR_AUTH_TOKEN = 'tr-auth-token';
const STORAGE_UDS_TOKEN = 'onvio-uds-token';
/**
 * Takes a {@link Subject} and returns an {@link Observable} that emits the same value but prevent
 * emitting the same value twice consequently.
 * @param obs - The {@link Subject} to share
 * @returns The {@link Observable} that emits the same value
 */
function distinctObs(obs) {
    return obs.asObservable().pipe(distinctUntilChanged());
}
/**
 * Handle both `401` and `403` as non-error with empty response
 * @param error - The error to handle (has to be a {@link HttpError} to be controlled)
 * @returns
 */
function unAuthControl(error) {
    if (error instanceof HttpError && [401, 403].includes(error.status)) {
        return undefined;
    }
    // istanbul ignore next
    throw error;
}
/**
 * Session manager that allows to interact with the an Onvio session
 * @public
 */
export class SessionManager {
    /**
     * Session manager that allows to interact with the an Onvio session
     * @param autoDetectSession - When `true` the session will be try to get auto detected using Cookies and SessionStorage.
     * If `false` the consumer will have to call any of the login methods to authenticate.
     */
    constructor(autoDetectSession = true) {
        /**
         * Holds all subscriptions that must be unsubscribed when the session is closed
         */
        this.subscriptions = new Subscription();
        /**
         * Holds and emit the current status of authentication
         */
        this._authenticated = new ReplaySubject(1);
        /**
         * Holds and emit the current UDS Token
         */
        this._currentUDSLongToken = new ReplaySubject(1);
        /**
         * Holds and emit the current JWT Token
         */
        this._currentJWT = new ReplaySubject(1);
        /**
         * Holds and emit the current session object
         */
        this._currentSession = new ReplaySubject(1);
        /**
         * `true` if the current session is persisted in the browser
         */
        this.isSessionPersisted = false;
        this.httpClient = new SecureHttp(this, 'onvio:session');
        this.environment = from(getEnvironmentV1());
        this.authenticated = distinctObs(this._authenticated);
        this.currentUDSLongToken = distinctObs(this._currentUDSLongToken);
        this.currentJWT = distinctObs(this._currentJWT);
        this.currentSession = distinctObs(this._currentSession);
        this.sessionAndBindings = this.authenticated.pipe(filter(authenticated => authenticated), switchMap(() => getSessionAndBindingsV1(this)), shareReplay(1));
        this.IACs = this.sessionAndBindings.pipe(map(getIACs), shareReplay(1));
        this.FRPs = this.sessionAndBindings.pipe(map(getFRPsV1), shareReplay(1));
        this.companyID = this.sessionAndBindings.pipe(map(getCompanyID), shareReplay(1));
        this.contactID = this.sessionAndBindings.pipe(map(getContactID), shareReplay(1));
        if (autoDetectSession) {
            this.initialize().catch(console.error);
        }
        else {
            this._authenticated.next(false);
        }
    }
    /** {@inheritDoc SessionManagerV1.hasSomeFRPs} */
    async hasSomeFRPs(permissions) {
        return lastValueFrom(this.sessionAndBindings.pipe(map(s => frps.hasSome(s.UdsSessionBindings.FeatureResourcePermissions.FeatureResourcePermissionsMap, permissions))));
    }
    /** {@inheritDoc SessionManagerV1.hasEveryFRPs} */
    async hasEveryFRPs(permissions) {
        return lastValueFrom(this.sessionAndBindings.pipe(map(s => frps.hasEvery(s.UdsSessionBindings.FeatureResourcePermissions.FeatureResourcePermissionsMap, permissions))));
    }
    /** {@inheritDoc SessionManagerV1.hasNotSomeFRPs} */
    async hasNotSomeFRPs(permissions) {
        return lastValueFrom(this.sessionAndBindings.pipe(map(s => frps.notSome(s.UdsSessionBindings.FeatureResourcePermissions.FeatureResourcePermissionsMap, permissions))));
    }
    /** {@inheritDoc SessionManagerV1.hasNotEveryFRPs} */
    async hasNotEveryFRPs(permissions) {
        return lastValueFrom(this.sessionAndBindings.pipe(map(s => frps.notEvery(s.UdsSessionBindings.FeatureResourcePermissions.FeatureResourcePermissionsMap, permissions))));
    }
    /** {@inheritDoc SessionManagerV1.hasSomeIACs} */
    async hasSomeIACs(controls) {
        return lastValueFrom(this.sessionAndBindings.pipe(map(s => iacs.hasSome(s.UdsSessionBindings.InfrastructureAccessControls, controls))));
    }
    /** {@inheritDoc SessionManagerV1.hasEveryIACs} */
    async hasEveryIACs(controls) {
        return lastValueFrom(this.sessionAndBindings.pipe(map(s => iacs.hasEvery(s.UdsSessionBindings.InfrastructureAccessControls, controls))));
    }
    /** {@inheritDoc SessionManagerV1.hasNotSomeIACs} */
    async hasNotSomeIACs(controls) {
        return lastValueFrom(this.sessionAndBindings.pipe(map(s => iacs.notSome(s.UdsSessionBindings.InfrastructureAccessControls, controls))));
    }
    /** {@inheritDoc SessionManagerV1.hasNotEveryIACs} */
    async hasNotEveryIACs(controls) {
        return lastValueFrom(this.sessionAndBindings.pipe(map(s => iacs.notEvery(s.UdsSessionBindings.InfrastructureAccessControls, controls))));
    }
    /** {@inheritDoc SessionManagerV1.hasFirmSomeLicense} */
    async hasFirmSomeLicense(licenses) {
        return lastValueFrom(this.sessionAndBindings.pipe(map(s => hasFirmSomeLicense(s.UdsSessionBindings.ApplicationConfiguration, licenses))));
    }
    /** {@inheritDoc SessionManagerV1.hasFirmEveryLicense} */
    async hasFirmEveryLicense(licenses) {
        return lastValueFrom(this.sessionAndBindings.pipe(map(s => hasFirmEveryLicense(s.UdsSessionBindings.ApplicationConfiguration, licenses))));
    }
    /** {@inheritDoc SessionManagerV1.getVerticalRoute} */
    async getVerticalRoute(vertical) {
        return lastValueFrom(this.sessionAndBindings.pipe(map(s => getVerticalRoute(s, vertical))));
    }
    /** {@inheritDoc SessionManagerV1.login} */
    async login(username, password, persist) {
        const session = await createSession(username, password).catch(unAuthControl);
        if (session) {
            this._currentUDSLongToken.next(session.uds);
            this._currentJWT.next(session.jwt);
            if (persist) {
                this.persistedInBrowser(session.uds, session.jwt);
            }
            this._authenticated.next(true);
            await this.startToCheckExpiration();
            return true;
        }
        return false;
    }
    /** {@inheritDoc SessionManagerV1.loginWithUDS} */
    async loginWithUDS(uds, persist) {
        const jwt = await createJWT(uds).catch(unAuthControl);
        if (jwt) {
            this._currentUDSLongToken.next(uds);
            this._currentJWT.next(jwt);
            if (persist) {
                this.persistedInBrowser(uds, jwt);
            }
            this._authenticated.next(true);
            await this.startToCheckExpiration();
            return true;
        }
        return false;
    }
    /** {@inheritDoc SessionManagerV1.loginWithJWT} */
    async loginWithJWT(jwt, persist) {
        const uds = await createSessionWithJWT(jwt).catch(unAuthControl);
        if (uds) {
            this._currentUDSLongToken.next(uds);
            this._currentJWT.next(jwt);
            if (persist) {
                this.persistedInBrowser(uds, jwt);
            }
            this._authenticated.next(true);
            await this.startToCheckExpiration();
            return true;
        }
        return false;
    }
    /** {@inheritDoc SessionManagerV1.loginWithSSO} */
    async loginWithSSO(sso, persist) {
        const uds = await retrieveSessionWithSSO(sso).catch(unAuthControl);
        if (uds) {
            return this.loginWithUDS(uds, persist);
        }
        return false;
    }
    /** {@inheritDoc SessionManagerV1.logout} */
    async logout() {
        const authenticated = await lastValueFrom(this.authenticated);
        if (!authenticated) {
            return false;
        }
        await deleteSession(this);
        if (this.isSessionPersisted) {
            globalThis.sessionStorage.removeItem(STORAGE_UDS_TOKEN);
            globalThis.sessionStorage.removeItem(STORAGE_TR_AUTH_TOKEN);
            globalThis.localStorage.removeItem(STORAGE_UDS_TOKEN);
            document.cookie = 'UDSLongToken=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
        }
        this._authenticated.next(false);
        this._currentUDSLongToken.next('');
        this._currentJWT.next('');
        this.subscriptions.unsubscribe();
        return true;
    }
    persistedInBrowser(uds, jwt) {
        globalThis.sessionStorage.setItem(STORAGE_TR_AUTH_TOKEN, jwt);
        globalThis.sessionStorage.setItem(STORAGE_UDS_TOKEN, uds);
        this.isSessionPersisted = true;
    }
    async initialize() {
        const storedUDS = this.getStoredUDS();
        if (storedUDS && (await this.loginWithUDS(storedUDS, true))) {
            return;
        }
        const storedJWT = this.getStoredAuthJWT();
        if (storedJWT && (await this.loginWithJWT(storedJWT, true))) {
            return;
        }
        this._authenticated.next(false);
    }
    /** {@inheritDoc SessionManagerV1.dispose} */
    dispose() {
        if (this.isSessionPersisted) {
            globalThis.sessionStorage.removeItem(STORAGE_UDS_TOKEN);
            globalThis.sessionStorage.removeItem(STORAGE_TR_AUTH_TOKEN);
            globalThis.localStorage.removeItem(STORAGE_UDS_TOKEN);
            document.cookie = 'UDSLongToken=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
        }
        this.subscriptions.unsubscribe();
        this._authenticated.complete();
        this._currentUDSLongToken.complete();
        this._currentJWT.complete();
        this._currentSession.complete();
        this.isSessionPersisted = false;
    }
    /**
     * This method creates two listeners that will check the session life time and logout the user if it is expired
     */
    async startToCheckExpiration() {
        const activeSessionChecker = this.currentSession.pipe(map(session => new Date(session.OrphanExpiresDateTime).getTime() - Date.now()), delayWhen(delay => timer(delay)), withLatestFrom(this.authenticated), filter(([, authenticated]) => authenticated), switchMap(() => getSessionV1(this)), switchMap(session => {
            const isExpired = new Date(session.OrphanExpiresDateTime).getTime() - Date.now() <= 0;
            if (isExpired) {
                return this.logout();
            }
            this._currentSession.next(session);
            return EMPTY;
        }), catchError(async () => this.logout()));
        const sessionExpirationChecker = this.currentSession.pipe(map(session => new Date(session.SessionExpiresDateTime)), delayWhen(delay => timer(delay)), switchMap(async () => this.logout()));
        this.subscriptions.add(activeSessionChecker.subscribe());
        this.subscriptions.add(sessionExpirationChecker.subscribe());
        const currentSession = await lastValueFrom(getSessionV1(this));
        this._currentSession.next(currentSession);
    }
    getStoredAuthJWT() {
        var _a;
        return (_a = globalThis.sessionStorage.getItem(STORAGE_TR_AUTH_TOKEN)) !== null && _a !== void 0 ? _a : undefined;
    }
    getStoredUDS() {
        var _a, _b, _c, _d;
        return ((_b = (_a = globalThis.sessionStorage.getItem(STORAGE_UDS_TOKEN)) !== null && _a !== void 0 ? _a : globalThis.localStorage.getItem(STORAGE_UDS_TOKEN)) !== null && _b !== void 0 ? _b : (_d = (_c = /UDSLongToken=(?<token>[a-zA-Z0-9\-_]*)/g.exec(document.cookie)) === null || _c === void 0 ? void 0 : _c.groups) === null || _d === void 0 ? void 0 : _d.token);
    }
}
/**
 * Default instance of {@link SessionManager}
 * @remarks It will be `undefined` during testing executions to avoid unexpected network requests
 */
/* istanbul ignore next: during testing no default managers will be used */
SessionManager.root = isTestEnv() ? undefined : new SessionManager();
/**
 * Default {@link SessionManager} instance
 * @public
 * @remarks It will be `undefined` during testing executions to avoid unexpected network requests
 */
export const sessionManager = SessionManager.root;
